import React from "react";
import { CheckCircle } from "lucide-react";
import {
  setOpenSignatureDialog,
  setOpenVaccineDialog,
  setShowAddVitalsModal,
  setCurrentOpenVitalsModal,
  setOpenBookingDialog,
  setOpenRefDialog,
  setVitalsDetails,
  setBookingDetails,
  setReferralLetter,
  setChatBotReferralLetter,
  setShowVaccinationPreviewModal,
  setVaccinationImageBase64,
  setOpenPrescriptionDialog,
  setDynamicPrescriptions,
  setPrescriptionId,
  setIsPrescriptionUpdate,
  setSelectedPrescriptionCategory,
  setPrescriptionViewerOpen,
  setEditingPrescription,
  getPatientConsentSignature,
  setViewOpen,
} from "../../../../../dispatchers/patientDispatchers";
import useTitanApp from "../../../../../hooks/useTitanApp";
import {
  getPatientPrescriptionById,
  getPrescriptionByPrescriptionId,
  setConsultationPrescriptionImagePreview,
  setPrescriptionImagePreview,
  setPreviewPrescription,
  setShowPrintPreviewLoading,
} from "../../../../../dispatchers/patientPrescriptionDispatcher";
import { usePrescriptionData } from "../../../../../lib/prescriptionData";
import { getPatientDocumentById } from "../../../../../dispatchers/patientDispatchers";
import { setViewingDoc } from "../../../../../dispatchers/documentDispatchers";

// Helper function to extract letter content from complex structures

function safeParsePayload(details) {
  if (!details) return details;

  if (typeof details === "string") {
    try {
      return JSON.parse(details);
    } catch {
      return details;
    }
  }

  if (details?.payload && typeof details.payload === "string") {
    try {
      const parsed = JSON.parse(details.payload);
      return parsed || details;
    } catch {
      return details;
    }
  }

  return details;
}

// Function to format time in "07:52 PM" format
function formatTime(dateString) {
  if (!dateString) return "";

  try {
    const date = new Date(dateString);

    // Check if the dateString is already in locale format (e.g., "1/15/2026, 7:02:33 PM")
    if (typeof dateString === "string" && dateString.includes(",")) {
      // Extract time part from locale string
      const timePart = dateString.split(",")[1]?.trim();
      if (timePart) {
        // Try to format just the time part
        const timeMatch = timePart.match(/(\d+):(\d+):(\d+)\s*(AM|PM)/i);
        if (timeMatch) {
          const hours = parseInt(timeMatch[1]);
          const minutes = timeMatch[2];
          const ampm = timeMatch[4];
          return `${hours}:${minutes} ${ampm}`;
        }
        return timePart;
      }
    }

    // Fallback to formatting the date object
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (e) {
    console.error("Error formatting time:", e);
    // If it's already in locale string format, extract just the time
    if (typeof dateString === "string" && dateString.includes(",")) {
      const parts = dateString.split(",");
      if (parts.length > 1) {
        return parts[1].trim();
      }
    }
    return dateString || "";
  }
}
// Function to get timestamp from details based on action type
function getTimestampFromDetails(actionId, parsedDetails) {
  if (!parsedDetails) return null;

  switch (actionId) {
    case 1: // Prescription
      return (
        parsedDetails.prescriptionDetails?.recordedAt ||
        parsedDetails.recordedAt
      );

    case 2: // Payment
      return (
        parsedDetails.paymentDetails?.recordedAt || parsedDetails.recordedAt
      );

    case 3: // Referral
      return (
        parsedDetails.letterDetails?.recordedAt || parsedDetails.recordedAt
      );

    case 4: // Appointment
      return parsedDetails.recordedAt;

    case 5: // Vitals
      if (parsedDetails.vitalsDetails) {
        return parsedDetails.vitalsDetails.recordedAt;
      }
      return parsedDetails.recordedAt;

    case 6: // Vaccine
      if (parsedDetails.vaccinationDetails) {
        return (
          parsedDetails.recordedAt ||
          parsedDetails.vaccinationDetails.recordedAt
        );
      }
      return (
        parsedDetails.recordedAt || parsedDetails.vaccineDetails?.recordedAt
      );

    case 9: // Signature
      return (
        parsedDetails.signatureDetails?.recordedAt || parsedDetails.signedAt
      );

    case 8: // Document
      return (
        parsedDetails.documentDetails?.recordedAt || parsedDetails.recordedAt
      );

    case 10: // Image
      return (
        parsedDetails.imageDetails?.timestamp ||
        parsedDetails.timestamp ||
        parsedDetails.createdAt
      );

    default:
      return (
        parsedDetails.timestamp ||
        parsedDetails.performedAt ||
        parsedDetails.recordedAt
      );
  }
}
function formatVitalLabel(vitalName) {
  if (!vitalName) return "";
  const normalized = vitalName?.charAt(0).toUpperCase() + vitalName?.slice(1);
  switch (normalized) {
    case "HeartRate":
      return "Heart Rate";
    case "BloodPressure":
      return "Blood Pressure";
    case "RespiratoryRate":
      return "Respiratory Rate";
    case "Bmi":
      return "BMI";
    case "Weight":
      return "Weight";
    case "Temperature":
      return "Temperature";
    case "Height":
      return "Height";
    case "Circumference":
      return "Circumference";
    default:
      return vitalName;
  }
}

function getVitalDataFromDetails(details) {
  if (!details || typeof details !== "object") return null;

  const vitalKeys = [
    "heartRate",
    "bloodPressure",
    "temperature",
    "respiratoryRate",
    "height",
    "weight",
    "circumference",
    "bmi",
  ];

  const foundVitals = vitalKeys.filter((key) => details[key]);
  if (foundVitals.length === 0) return null;

  if (details.recordedVitalType && details[details.recordedVitalType]) {
    const key = details.recordedVitalType;

    // Special handling for blood pressure
    if (key === "bloodPressure" && details.bloodPressure) {
      const bp = details.bloodPressure;
      const systolicValue =
        bp.Systolic?.Value || bp.systolic?.Value || bp.Systolic || bp.systolic;
      const diastolicValue =
        bp.Diastolic?.Value ||
        bp.diastolic?.Value ||
        bp.Diastolic ||
        bp.diastolic;

      return {
        vitalKey: key,
        vitalData: bp,
        vitalType: "Blood Pressure",
        displayValue: `${systolicValue || "--"}/${diastolicValue || "--"} mmHg`,
      };
    }

    // Special handling for height
    if (key === "height" && details.height) {
      const height = details.height;
      // Check if height is in cm and add unit
      let displayValue = height.Value || "--";
      if (height.Unit === "cm" && displayValue !== "--") {
        displayValue = `${displayValue} cm`;
      }
      return {
        vitalKey: key,
        vitalData: height,
        vitalType: "Height",
        displayValue: displayValue,
      };
    }

    return {
      vitalKey: key,
      vitalData: details[key],
      vitalType: formatVitalLabel(key),
    };
  }

  if (foundVitals.length === 1) {
    const key = foundVitals[0];

    // Special handling for blood pressure
    if (key === "bloodPressure" && details.bloodPressure) {
      const bp = details.bloodPressure;
      const systolicValue =
        bp.Systolic?.Value || bp.systolic?.Value || bp.Systolic || bp.systolic;
      const diastolicValue =
        bp.Diastolic?.Value ||
        bp.diastolic?.Value ||
        bp.Diastolic ||
        bp.diastolic;

      return {
        vitalKey: key,
        vitalData: bp,
        vitalType: "Blood Pressure",
        displayValue: `${systolicValue || "--"}/${diastolicValue || "--"} mmHg`,
      };
    }

    // Special handling for height
    if (key === "height" && details.height) {
      const height = details.height;
      return {
        vitalKey: key,
        vitalData: height,
        vitalType: "Height",
        displayValue: height.Value || "--",
      };
    }

    return {
      vitalKey: key,
      vitalData: details[key],
      vitalType: formatVitalLabel(key),
    };
  }

  if (foundVitals.includes("bmi")) {
    const bmi = details["bmi"];
    const weight = details["weight"];
    const height = details["height"];

    if (bmi?.Value && bmi?.Unit && (!weight?.Value || !height?.Value)) {
      return {
        vitalKey: "bmi",
        vitalData: bmi,
        vitalType: formatVitalLabel("bmi"),
      };
    }

    if (foundVitals.includes("weight")) {
      return {
        vitalKey: "weight",
        vitalData: weight,
        vitalType: formatVitalLabel("weight"),
      };
    }

    if (foundVitals.includes("height")) {
      const height = details["height"];
      return {
        vitalKey: "height",
        vitalData: height,
        vitalType: "Height",
        displayValue: height.Value || "--",
      };
    }
  }

  const key = foundVitals[0];

  // Special handling for blood pressure
  if (key === "bloodPressure" && details.bloodPressure) {
    const bp = details.bloodPressure;
    const systolicValue =
      bp.Systolic?.Value || bp.systolic?.Value || bp.Systolic || bp.systolic;
    const diastolicValue =
      bp.Diastolic?.Value ||
      bp.diastolic?.Value ||
      bp.Diastolic ||
      bp.diastolic;

    return {
      vitalKey: key,
      vitalData: bp,
      vitalType: "Blood Pressure",
      displayValue: `${systolicValue || "--"}/${diastolicValue || "--"} mmHg`,
    };
  }

  // Special handling for height
  if (key === "height" && details.height) {
    const height = details.height;
    return {
      vitalKey: key,
      vitalData: height,
      vitalType: "Height",
      displayValue: height.Value || "--",
    };
  }

  return {
    vitalKey: key,
    vitalData: details[key],
    vitalType: formatVitalLabel(key),
  };
}

function SuccessActionMessage({
  actionId,
  details,
  isLatest,
  isDarkMode,
  isDraft,
  timestamp: manualTimestamp,
  messageId,
  vitalMessageId,
}) {
  const { patientState, appState, dispatch } = useTitanApp();
  const { companyId } = appState;
  const {
    selectedPatientConsultation,
    consultationId,
    patientConsentSignature,
    patientDocumentDetails,
  } = patientState || {};
  const parsedDetails = safeParsePayload(details);
  const vitalsData = parsedDetails?.vitalsDetails || parsedDetails;
  const prescriptionData =
    actionId === 1 ? parsedDetails?.prescriptionDetails || parsedDetails : null;

  // Get the actual timestamp from the details
  const timestamp =
    manualTimestamp || getTimestampFromDetails(actionId, parsedDetails);

  // Format the display time using the actual timestamp from the data
  const displayTime = formatTime(timestamp);

  const vitalInfo = actionId === 5 ? getVitalDataFromDetails(vitalsData) : null;
  const handleShowPrescription = async (prescriptionId) => {
    setPreviewPrescription(true);
    setShowPrintPreviewLoading(true);
    const result = await getPrescriptionByPrescriptionId(prescriptionId);
    setConsultationPrescriptionImagePreview(result?.base64Image || "");
    setShowPrintPreviewLoading(false);
  };
  const actionTitles = {
    1: "Prescription",
    2: "Payment",
    3: "Referral Letter",
    4: "Appointment",
    5: "Vitals Recorded",
    6: "Vaccination",
    8: "Document Added",
    9: "Signature Added",
    "prescription-draft": "Prescription Draft",
    "vaccine-draft": "Vaccine Draft",
    11: "Prescription Draft",
    12: "Vaccine Draft",
  };

  const shouldActionBeEnabled = () => {
    if (actionId === 2) return false;
    if (actionId === 1) return isLatest !== false;
    if (actionId === 8) return isLatest === true;
    if (
      actionId === 3 ||
      actionId === 4 ||
      actionId === 6 ||
      isDraft ||
      actionId === 11 ||
      actionId === "11" ||
      actionId === 12 ||
      actionId === "12"
    )
      return true;
    if (actionId === "prescription-draft" || actionId === "vaccine-draft")
      return true;
    return isLatest;
  };

  const isActionEnabled = shouldActionBeEnabled();

  const handleViewClick = async () => {
    if (!isActionEnabled) return;

    if (actionId === 1) {
      setIsPrescriptionUpdate(true);
      if (prescriptionData) {
        await handleShowPrescription(prescriptionData.prescriptionId);
      }
    }
    if (
      actionId === "prescription-draft" ||
      actionId === 11 ||
      actionId === "11"
    ) {
      setOpenPrescriptionDialog(true);
      setPrescriptionId(null);
      setIsPrescriptionUpdate(false);
      setSelectedPrescriptionCategory("private");
      if (parsedDetails?.prescriptionItems) {
        const mappedItems = parsedDetails?.prescriptionItems?.map((item) => {
          let qty = item.quantity;
          if (typeof qty === "string") {
            const match = qty.match(/(\d+)/);
            if (match) qty = match[1];
          }
          const hasId = !item.prescribedItemId?.startsWith("k9x_");
          return {
            ...item,
            value: hasId ? item.value : "",
            prescribedItemId: hasId ? item.prescribedItemId : "",
            quantity: qty,
          };
        });
        setDynamicPrescriptions(mappedItems);
      }
    }
    if (actionId === "vaccine-draft" || actionId === 12 || actionId === "12") {
      setOpenVaccineDialog(true);
      // setVaccineDetails(parsedDetails);
    }
    if (actionId === 3) {
      setReferralLetter(details);
      setOpenRefDialog(true);
      setChatBotReferralLetter(details);
    }

    if (actionId === 4) {
      let originalAppointmentId = null;
      try {
        const parsedDetails = safeParsePayload(details);
        if (parsedDetails?.id) {
          originalAppointmentId = parsedDetails.id;
        }
      } catch (e) {
        console.warn("Could not parse appointment ID for edit:", e);
      }

      setBookingDetails({
        ...details,
        _originalAppointmentId: originalAppointmentId,
      });
      setOpenBookingDialog(true);
    }
    if (actionId === 5) {
      if (selectedPatientConsultation?.consultationStatus === 1) return;

      const modalVitalData = {
        vitalName: vitalInfo?.vitalType,
        ...vitalInfo?.vitalData,
      };

      if (vitalInfo?.vitalKey === "bloodPressure") {
        // For blood pressure, extract systolic and diastolic properly
        modalVitalData.systolic =
          vitalInfo?.systolicValue ||
          vitalInfo?.vitalData?.Systolic?.Value ||
          vitalInfo?.vitalData?.systolic?.Value;
        modalVitalData.diastolic =
          vitalInfo?.diastolicValue ||
          vitalInfo?.vitalData?.Diastolic?.Value ||
          vitalInfo?.vitalData?.diastolic?.Value;
        modalVitalData.unit = "mmHg";
      } else {
        modalVitalData.value = vitalInfo?.vitalData?.Value;
        modalVitalData.unit = vitalInfo?.vitalData?.Unit;
      }

      setCurrentOpenVitalsModal({
        isEdit: true,
        vital: modalVitalData,
        vitalType: vitalInfo?.vitalType,
        actionId: actionId,
        vitalMessageId: vitalMessageId,
      });
    }
    if (actionId === 6) {
      setVaccinationImageBase64(details);
      setShowVaccinationPreviewModal(true);
    }
    if (actionId === 9) {
      let payload = {
        consultationId,
        signatureId: details.signatureId,
      };
      await getPatientConsentSignature(payload);
      setOpenSignatureDialog({ open: true, viewMode: true, details });
    }
    if (actionId === 8) {
      try {
        const innerData = details?.payload ? JSON.parse(details.payload) : null;
        const documentId = innerData?.payload?.documentId;

        if (!documentId) {
          return;
        }

        const result = await getPatientDocumentById(documentId);

        const documentContent = result?.base64Content;
        let extension = result?.fileExtenstion;

        if (documentContent) {
          // Ensure extension has a leading dot
          if (extension && !extension.startsWith(".")) {
            extension = "." + extension;
          }

          const mimeMap = {
            ".pdf": "application/pdf",
            ".docx":
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            ".doc": "application/msword",
            ".png": "image/png",
            ".jpg": "image/jpeg",
            ".jpeg": "image/jpeg",
          };

          const mimeType =
            mimeMap[extension?.toLowerCase()] || "application/octet-stream";

          let pureBase64 = documentContent;
          if (
            typeof documentContent === "string" &&
            documentContent.startsWith("data:")
          ) {
            pureBase64 = documentContent.split(",")[1];
          }

          try {
            const byteCharacters = atob(pureBase64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: mimeType });
            const blobUrl = URL.createObjectURL(blob);

            setViewingDoc({
              title:
                innerData?.payload?.fileName ||
                `Document${extension || ".pdf"}`,
              url: blobUrl,
              mimeType: mimeType,
            });
            setViewOpen(true);
          } catch (atobError) {
            console.error("Invalid base64 string provided", atobError);
          }
        } else {
          console.error("Document content is empty or null");
        }
      } catch (error) {
        console.error("Error loading document:", error);
      }
    }
  };
  const formatSignedTime = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (e) {
      return dateString;
    }
  };

  function formatAppointmentDateTime(appointmentData) {
    if (!appointmentData) return { date: "", time: "" };

    try {
      const startDate = appointmentData.startDate
        ? new Date(appointmentData.startDate)
        : null;

      if (!startDate || isNaN(startDate.getTime())) {
        return { date: "", time: "" };
      }

      const formattedDate = startDate.toISOString().split("T")[0];

      const hours = startDate.getHours();
      const minutes = startDate.getMinutes().toString().padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      const formattedHours = hours % 12 || 12;
      const formattedTime = `${formattedHours}:${minutes} ${ampm}`;

      return {
        date: formattedDate,
        time: formattedTime,
      };
    } catch (error) {
      return { date: "", time: "" };
    }
  }

  const appointmentDateTime =
    actionId === 4 ? formatAppointmentDateTime(parsedDetails) : null;

  if (!parsedDetails) return null;

  return (
    <div
      className={`mb-3 last:mb-0 ${
        isDarkMode ? "bg-gray-800" : "bg-white"
      } rounded-lg border ${
        isDraft
          ? isDarkMode
            ? "border-orange-600/50 bg-orange-900/10"
            : "border-orange-300 bg-orange-900/10"
          : isDarkMode
            ? "border-gray-700"
            : "border-gray-200"
      }`}
    >
      <div
        className="transition-all cursor-pointer"
        onClick={isActionEnabled ? handleViewClick : undefined}
      >
        {/* Header Section with Action Title */}
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle
                className={`w-4 h-4 ${
                  isDarkMode ? "text-green-400" : "text-green-600"
                }`}
              />
              <span
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-100" : "text-gray-800"
                }`}
              >
                {actionTitles[actionId]}
              </span>
              {isDraft && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-500 text-white uppercase tracking-wider">
                  Draft
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <span
                className={`text-xs ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {displayTime}
              </span>
            </div>
          </div>
        </div>

        {/* Content Section - NO ICONS, just text */}
        <div className="px-4 pb-3">
          {/* REFERRAL LETTER - Action 3 */}
          {actionId === 3 && parsedDetails && (
            <div className="space-y-2">
              <div>
                <p
                  className={`text-sm font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  } mb-1`}
                >
                  Type: Referral Letter -{" "}
                  {parsedDetails?.title || "Consultation"}
                </p>
                <p
                  className={`text-xs ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Recipients: {parsedDetails?.recipients?.length} email(s)
                </p>
              </div>
            </div>
          )}

          {/* VITALS - Action 5 */}
          {actionId === 5 && vitalInfo && (
            <div className="space-y-2">
              <div>
                {/* For Blood Pressure */}
                {vitalInfo.vitalKey === "bloodPressure" && (
                  <p
                    className={`text-sm font-medium ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    } mb-1`}
                  >
                    {vitalInfo.vitalType}: {vitalInfo.displayValue}
                  </p>
                )}

                {/* For Height */}
                {vitalInfo.vitalKey === "height" && (
                  <p
                    className={`text-sm font-medium ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    } mb-1`}
                  >
                    {vitalInfo.vitalType}: {vitalInfo.displayValue}
                  </p>
                )}

                {/* For other vitals */}
                {vitalInfo.vitalKey !== "bloodPressure" &&
                  vitalInfo.vitalKey !== "height" && (
                    <p
                      className={`text-sm font-medium ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      } mb-1`}
                    >
                      {vitalInfo.vitalType}:{" "}
                      {vitalInfo.vitalData?.Value || "--"}{" "}
                      {vitalInfo.vitalData?.Unit || ""}
                    </p>
                  )}

                {/* Only show "Recorded at" if we have the timestamp */}
                {timestamp && (
                  <p
                    className={`text-xs ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Recorded at: {displayTime}
                  </p>
                )}
              </div>
            </div>
          )}
          {/* APPOINTMENT - Action 4 */}
          {actionId === 4 && appointmentDateTime && (
            <div className="space-y-2">
              <div>
                <div className="space-y-1">
                  <p
                    className={`text-sm font-medium ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Date: {appointmentDateTime.date}
                  </p>
                  <p
                    className={`text-sm font-medium ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Time: {appointmentDateTime.time}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* VACCINATION - Action 6 */}
          {actionId === 6 && (
            <div className="space-y-2">
              <div>
                <p
                  className={`text-sm font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  } mb-1`}
                >
                  Generated: {displayTime}
                </p>
              </div>
            </div>
          )}

          {/* PRESCRIPTION - Action 1 & Draft */}
          {(actionId === 1 ||
            actionId === "prescription-draft" ||
            actionId === 11 ||
            actionId === "11") &&
            (prescriptionData?.drugs || parsedDetails?.prescriptionItems) &&
            (prescriptionData?.drugs?.length > 0 ||
              parsedDetails?.prescriptionItems?.length > 0) && (
              <div className="space-y-2">
                <div>
                  {(prescriptionData?.drugs || parsedDetails?.prescriptionItems)
                    .slice(0, 2)
                    .map((drug, index) => (
                      <div key={index} className="mb-2 last:mb-0">
                        <p
                          className={`text-sm font-medium ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          {drug?.drugName || drug?.medication}{" "}
                          {drug?.dose || drug?.dosage || ""}
                          {drug?.quantity ? ` • Qty: ${drug.quantity}` : ""}
                        </p>
                        {(drug?.note || drug?.notes) && (
                          <p
                            className={`text-xs ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            } italic mt-0.5`}
                          >
                            Note: {drug?.note || drug?.notes}
                          </p>
                        )}
                      </div>
                    ))}
                  {(prescriptionData?.drugs || parsedDetails?.prescriptionItems)
                    .length > 2 && (
                    <p
                      className={`text-xs ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      +
                      {(
                        prescriptionData?.drugs ||
                        parsedDetails?.prescriptionItems
                      ).length - 2}
                      more medications
                    </p>
                  )}
                </div>
              </div>
            )}

          {/* VACCINE DRAFT - Action 12 */}
          {(actionId === "vaccine-draft" ||
            actionId === 12 ||
            actionId === "12") &&
            (parsedDetails?.vaccineItems || parsedDetails?.vaccineName) && (
              <div className="space-y-2">
                {parsedDetails?.vaccineItems ? (
                  parsedDetails.vaccineItems.map((v, index) => (
                    <div key={index} className="mb-2 last:mb-0">
                      <p
                        className={`text-sm font-medium ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        {v.vaccineName} {v.dose}
                      </p>
                      {v.notes && (
                        <p
                          className={`text-xs ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          } italic mt-0.5`}
                        >
                          Note: {v.notes}
                        </p>
                      )}
                    </div>
                  ))
                ) : (
                  <div>
                    <p
                      className={`text-sm font-medium ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      {parsedDetails?.vaccineName} {parsedDetails?.dose}
                    </p>
                    {parsedDetails?.notes && (
                      <p
                        className={`text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        } italic mt-0.5`}
                      >
                        Note: {parsedDetails?.notes}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          {actionId === 2 && (
            <div className="space-y-2">
              <div>
                <p
                  className={`text-sm font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Amount:{" "}
                  {parsedDetails?.amount ? `£${parsedDetails.amount}` : ""}
                </p>
              </div>
            </div>
          )}

          {/* SIGNATURE - Action 8 */}
          {actionId === 9 && (
            <div className="space-y-2">
              <p
                className={`text-sm font-medium ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Signer:{" "}
                {(parsedDetails?.patientRelationStatus?.name || "")
                  .replace(/\bnull\b/gi, "")
                  .trim()}
              </p>
              <p
                className={`text-sm font-medium ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Relationship:{" "}
                {parsedDetails?.patientRelationStatus?.relationship || "Self"}
              </p>
              <div
                className={`mt-2 p-3 rounded ${
                  isDarkMode ? "bg-gray-900/50" : "bg-gray-100"
                }`}
              >
                <p
                  className={`text-sm font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  } mb-1`}
                >
                  Consent:
                </p>
                <p
                  className={`text-xs italic ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  "
                  {parsedDetails?.consentStatement ||
                    "I consent to the pharmacist providing the clinical services discussed and agree to the treatment plan outlined."}
                  "
                </p>
              </div>
              <p
                className={`text-xs ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Signed at: {formatTime(parsedDetails?.signedAt)}
              </p>
            </div>
          )}

          {/* DOCUMENT - Action 9 */}
          {actionId === 8 && (
            <div className="space-y-2">
              <p
                className={`text-sm font-medium ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                File:{" "}
                {parsedDetails?.title || parsedDetails?.fileName || "Document"}
              </p>
              <p
                className={`text-sm font-medium ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Source: {parsedDetails?.description || "Uploaded file"}
              </p>
              {parsedDetails?.size && (
                <p
                  className={`text-xs ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Size: {parsedDetails.size}
                </p>
              )}
              <p
                className={`text-xs ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Uploaded at: {displayTime}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SuccessActionMessage;
